import type { TFunction } from 'i18next'
import { BookOpen, Calendar, FileDown, Image as ImageIcon, Megaphone, Shapes } from 'lucide-react'
import type { ItemRow } from '@/types/spaceLayout'
import type { Event } from '@/types/events'
import type { CourseListItem } from '@/types/courses'
import type { DownloadItem } from '@/types/downloads'
import type { ClassifiedAd } from '@/types/classifieds'
import type { Content } from '@/types/content'
import type { GalleryImage } from '@/types/gallery'
import { imageUrl } from '@/lib/images'

/*
 * F-198, objection 1: each module turns its item into the row the List and Compact layouts draw
 * (thumbnail, title, two lines of metadata, the datum that decides, padlock, link). The registry
 * points at these (`layouts.toRow`); the pages call them directly.
 */

export interface RowContext {
  t: TFunction
  locale: string
  /** The community's zone: dates are the community's days (`zonedDay.ts`). */
  timeZone?: string
  /** `/{section}/{space}`, the base of an item's page. */
  basePath: string
}

const thumb = (url?: string | null) => (url ? imageUrl(url, { w: 192, h: 128, fit: 'crop' }) || url : null)

const plain = (html?: string | null, max = 140) => {
  const text = String(html ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text
}

const zone = (timeZone?: string) => {
  if (!timeZone) return undefined
  try {
    new Intl.DateTimeFormat('en-US', { timeZone })
    return timeZone
  } catch {
    return undefined
  }
}

const dateTime = (iso: string | null | undefined, ctx: RowContext, withTime = true) => {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat(ctx.locale, {
    day: '2-digit', month: 'short', ...(withTime ? { hour: '2-digit', minute: '2-digit' } : { year: 'numeric' }), timeZone: zone(ctx.timeZone)
  }).format(date)
}

const money = (amount: number | null | undefined, currency: string | null | undefined, locale: string) => {
  if (amount === null || amount === undefined || !Number.isFinite(Number(amount))) return null
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'BRL' }).format(Number(amount))
  } catch {
    return String(amount)
  }
}

export const eventRow = (event: Event, ctx: RowContext): ItemRow => ({
  id: event.id,
  title: event.title || ctx.t('events:editor.untitled'),
  href: `${ctx.basePath}/${event.slug || event.id}`,
  thumbnail: thumb(event.featuredImageUrl || event.bannerUrl),
  icon: Calendar,
  decisive: dateTime(event.startTime, ctx) ?? ctx.t('common:spaceLayout.noDate'),
  meta: [event.location || null, plain(event.description, 120) || null],
  locked: Boolean(event.requiresPlan || event.requiresPurchase),
  badge: event.status === 'DRAFT' ? ctx.t('common:spaceLayout.draft') : null
})

export const courseRow = (course: CourseListItem, ctx: RowContext & { href?: string | null }): ItemRow => ({
  id: course.id,
  title: course.title,
  href: ctx.href ?? `${ctx.basePath}/${course.slug}`,
  thumbnail: thumb(course.bannerUrl),
  icon: BookOpen,
  decisive: typeof course.userProgress?.percentage === 'number' ? `${Math.round(course.userProgress.percentage)}%` : null,
  meta: [
    ctx.t('common:spaceLayout.lessons', { count: course.lessonsCount ?? 0 }),
    course.instructor?.name || plain(course.description, 120) || null
  ],
  badge: course.status && course.status !== 'published' ? ctx.t(`courses:status.${course.status}`, { defaultValue: course.status }) : null
})

export const downloadRow = (item: DownloadItem, ctx: RowContext): ItemRow => ({
  id: item.id,
  title: item.title,
  href: `${ctx.basePath}/${item.id}`,
  thumbnail: thumb(item.coverUrl),
  icon: FileDown,
  decisive: item.product && Number(item.product.price) > 0 ? money(Number(item.product.price), item.product.currency, ctx.locale) : null,
  meta: [
    [item.category?.name, ctx.t('common:spaceLayout.files', { count: item.filesCount ?? 0 })].filter(Boolean).join(' · '),
    plain(item.description, 120) || null
  ],
  locked: item.locked
})

export const classifiedRow = (ad: ClassifiedAd, ctx: RowContext & { href: string; showValue: boolean }): ItemRow => ({
  id: ad.id,
  title: ad.title,
  href: ctx.href,
  thumbnail: thumb(ad.images?.[0]?.thumbnailUrl || ad.images?.[0]?.imageUrl || null),
  icon: Megaphone,
  decisive: ctx.showValue ? money(ad.valueAmount, ad.valueCurrency, ctx.locale) ?? (ad.valueNote || null) : null,
  meta: [
    [ad.category?.name, ad.author?.name].filter(Boolean).join(' · '),
    plain(ad.description, 120) || null
  ]
})

export const contentRow = (content: Content, ctx: RowContext): ItemRow => ({
  id: content.id,
  title: content.title || plain(content.content, 80) || ctx.t('common:spaceLayout.untitled'),
  href: `${ctx.basePath}/${content.id}`,
  thumbnail: thumb(content.coverImage),
  icon: Shapes,
  decisive: dateTime(content.publishedAt || content.createdAt, ctx, false),
  meta: [
    [content.author?.name, ctx.t('common:spaceLayout.comments', { count: content.commentsCount ?? 0 })].filter(Boolean).join(' · '),
    content.title ? plain(content.content, 120) || null : null
  ]
})

export const galleryRow = (image: GalleryImage, ctx: RowContext & { onOpen: () => void }): ItemRow => ({
  id: image.id,
  title: image.title || ctx.t('gallery:imageAlt'),
  onOpen: ctx.onOpen,
  thumbnail: thumb(image.thumbnailUrl || image.imageUrl),
  icon: ImageIcon,
  meta: [image.description || null, null]
})
